import { animate, style, transition, trigger } from '@angular/animations';
import { Component, HostListener, OnInit } from '@angular/core';
import { SharedModule } from '@App/shared/shared.module';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatBadgeModule } from '@angular/material/badge';
import { AppService } from '@App/app.service';
import { CaseSummaryService, SignalRService, UserAdminService } from '@Services';
import { HubConnection } from '@microsoft/signalr';
import { CaseHistory, CaseHistoryPayload } from '@Models';

@Component({
    selector: 'app-case-history-dropdown',
    templateUrl: './case-history-dropdown.component.html',
    styleUrls: ['./case-history-dropdown.component.scss'],
    imports: [SharedModule, MatFormFieldModule, MatInputModule, MatIconModule, MatBadgeModule],
    animations: [
        trigger('fadeIn', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateY(-5px)' }),
                animate('150ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
            ]),
            transition(':leave', [
                animate('100ms ease-in', style({ opacity: 0, transform: 'translateY(-5px)' })),
            ])
        ])
    ]
})
export class CaseHistoryDropdownComponent implements OnInit {

    userId: string;
    filterValue = '';
    isCaseHistoryDropdownOpen = false;
    caseHistory: CaseHistory[] = [];
    filteredCaseHistory: CaseHistory[] = [];
    signalrConnection: HubConnection;

    constructor(
        public appService: AppService,
        public userAdminService: UserAdminService,
        public caseSummaryService: CaseSummaryService,
        public signalrService: SignalRService,
    ) 
    {

    }

    ngOnInit() {
        this.userId = this.appService.getUserId();
        this.loadCaseHistory();
        this.setupSignalR();
    }

    loadCaseHistory() {
        this.userAdminService.getCaseHistory().subscribe((caseHistory: CaseHistory[]) => {
            this.caseHistory = caseHistory;
            this.applyFilterCaseHistory();
        });
    }

    applyFilterCaseHistory() {
        this.filteredCaseHistory = [];
        if (!this.filterValue) {
            this.filteredCaseHistory = [...this.caseHistory];
            return;
        }

        this.filteredCaseHistory = this.caseHistory.filter(c => {
            return c.caseNumberDisplay.toLowerCase().includes(this.filterValue.toLowerCase());
        });
    }

    toggleDropdown() {
        this.isCaseHistoryDropdownOpen = !this.isCaseHistoryDropdownOpen;

        if(this.isCaseHistoryDropdownOpen) {
            this.filterValue = '';
            this.applyFilterCaseHistory();
        }
    }

    viewCaseDetails(record, newTab = true) {
        record.accountCode = record.clientCode + '-' + record.accountCode;
        this.caseSummaryService.routeToCase(record, newTab);
    }

    updateFilteredCaseHistory(value: string) {
        this.filterValue = value;
        this.applyFilterCaseHistory();
    }

    @HostListener('document:click', ['$event'])
    onClickOutside(event: Event) {
        const target = event.target as HTMLElement;
        if (!this.isCaseHistoryDropdownOpen || target.closest('.case-history-container'))
            return;

        this.isCaseHistoryDropdownOpen = false;
    }

    //#region SignalR
    setupSignalR() {
        // Create HubConnection
        this.signalrConnection = this.signalrService.getUserSignalRConnection(this.userId);
        this.signalrConnection.serverTimeoutInMilliseconds = 1000 * 60 * 2; // 2 minutes

        // Setup Listeners

        this.signalrConnection.on('connected', (event) => {
            console.log('SignalR Connected for Case History : ' + event);
        });

        this.signalrConnection.on('caseHistory', (payload: CaseHistoryPayload) => {
            const incoming = payload.caseHistory;
            if (!incoming) return;

            incoming.forEach(newCase => {
                if (newCase.userId != this.userId) return; // a failsafe just in case backend sends case history for other users
                const index = this.caseHistory.findIndex(c => c.caseId === newCase.caseId);

                if (index > -1) {
                    this.caseHistory.splice(index, 1);
                } else if (this.caseHistory.length >= 10) {
                    this.caseHistory.pop();
                }
                this.caseHistory.unshift(newCase);
            });
            this.applyFilterCaseHistory();
        });

        this.signalrConnection.onclose(this.signalrOnClose.bind(this));

        // Start SignalR
        this.signalrConnection.start().then(this.signalrStarted.bind(this)).catch(this.signalrStartError);
    }

    signalrStarted() {
        console.log('CaseHistoryDropdown Component | SignalR connection started');
    }

    signalrStartError(error: any) {
        console.error(error.message);
    }

    signalrOnClose(error: any) {
        console.log('CaseHistoryDropdown Component | SignalR Disconnected');
    }
    //#endregion
}
